import {
	ADMIN_TEMPLATES_AND_BLUEPRINTS,
	SPACE_SETTINGS_TEMPLATES,
} from '@confluence/named-routes/entry-points/routes.ts';

export const IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME = 'inEditorTemplatesPanel';
export const IN_EDITOR_TEMPLATES_QUERY_OPEN = 'open';

export const IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN = 'keep_open';
export const IN_EDITOR_TEMPLATES_QUERY_AUTO_CLOSED = 'auto_closed';

export const CREATED_WITH_TEMPLATE_QUERY_PARAM_NAME = 'createdWithTemplate';

export const CONTEXT_PANEL_TAB_TARGET = 'contextPanelTabTarget';

// Tab target values (used in query params)
export const CONTEXT_PANEL_TAB_TARGET_TEMPLATES = 'templates';
export const CONTEXT_PANEL_TAB_TARGET_BLOCK = 'block';
export const CONTEXT_PANEL_TAB_TARGET_IMPORT = 'import';

// Panel mode values (used in component state)
export const PANEL_MODE_TEMPLATES = 'templates';
export const PANEL_MODE_IMPORT = 'import';

export type ContextPanelTabTarget =
	| typeof CONTEXT_PANEL_TAB_TARGET_TEMPLATES
	| typeof CONTEXT_PANEL_TAB_TARGET_BLOCK
	| typeof CONTEXT_PANEL_TAB_TARGET_IMPORT;

export type PanelMode = typeof PANEL_MODE_TEMPLATES | typeof PANEL_MODE_IMPORT;

type inEditorTemplatesPanel =
	| typeof IN_EDITOR_TEMPLATES_QUERY_OPEN
	| typeof IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN
	| typeof IN_EDITOR_TEMPLATES_QUERY_AUTO_CLOSED;

// Checks if the page was created with a template by reading the 'createdWithTemplate' query param
export const getCreatedWithTemplateQueryParamValue = (getQueryParams: any): boolean => {
	return getQueryParams()[CREATED_WITH_TEMPLATE_QUERY_PARAM_NAME] === 'true';
};

// Gets the value of the 'inEditorTemplatesPanel' query param from the URL
export const getInEditorTemplatesPanelQueryParamValue = (getQueryParams: any): any => {
	return getQueryParams()[IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME];
};

// Gets the value of the 'contextPanelTabTarget' query param from the URL
export const getTemplatesPanelTabTargetQueryParamValue = (getQueryParams: any): any => {
	return getQueryParams()[CONTEXT_PANEL_TAB_TARGET];
};

// Checks if the inEditorTemplatesPanel query param is set to 'open'
export const isInEditorTemplatesPanelQueryParamValueOpen = (getQueryParams: any): boolean => {
	return (
		getInEditorTemplatesPanelQueryParamValue(getQueryParams) === IN_EDITOR_TEMPLATES_QUERY_OPEN
	);
};

// Checks if the inEditorTemplatesPanel query param is open or keep open
export const isInEditorTemplatesPanelQueryParamValueOpenOrKeepOpen = (
	getQueryParams: any,
): boolean => {
	const queryParamValue = getInEditorTemplatesPanelQueryParamValue(getQueryParams);
	return (
		queryParamValue === IN_EDITOR_TEMPLATES_QUERY_OPEN ||
		queryParamValue === IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN
	);
};

// Checks if the inEditorTemplatesPanel query param is open or keep open and the templates panel mode is active
export const isInEditorTemplatesPanelQueryParamValueOpenOrKeepOpenWithTemplatesPanelMode = (
	getQueryParams: any,
): boolean => {
	const queryParamValue = getInEditorTemplatesPanelQueryParamValue(getQueryParams);
	const tabTarget = getTemplatesPanelTabTargetQueryParamValue(getQueryParams);
	return (
		(queryParamValue === IN_EDITOR_TEMPLATES_QUERY_OPEN ||
			queryParamValue === IN_EDITOR_TEMPLATES_QUERY_KEEP_OPEN) &&
		(tabTarget === CONTEXT_PANEL_TAB_TARGET_TEMPLATES ||
			tabTarget === CONTEXT_PANEL_TAB_TARGET_BLOCK)
	);
};

// Removes both the panel visibility and tab target query parameters.
export const removeInEditorTemplatesQueryParam = (
	setQueryParams: any,
	_isRovoLLMEnabled = false,
): void => {
	const paramsToRemove: Record<string, null> = {
		[IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME]: null,
	};

	paramsToRemove[CONTEXT_PANEL_TAB_TARGET] = null;

	setQueryParams(paramsToRemove, true);
};

// Sets panel visibility and, when provided, the tab target query parameter.
export const setInEditorTemplatesPanelQueryParam = (
	setQueryParams: any,
	inEditorTemplatesPanel: inEditorTemplatesPanel,
	tabTarget?: ContextPanelTabTarget,
	_isRovoLLMEnabled = false,
): void => {
	const params: Record<string, string> = {
		[IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME]: inEditorTemplatesPanel,
	};

	if (tabTarget) {
		params[CONTEXT_PANEL_TAB_TARGET] = tabTarget;
	}

	setQueryParams(params, true);
};

export const getTemplateListPage = (spaceKey?: string): string => {
	if (spaceKey) {
		return SPACE_SETTINGS_TEMPLATES.toUrl({ spaceKey });
	}

	return ADMIN_TEMPLATES_AND_BLUEPRINTS.toUrl();
};
